import React, { useEffect, useState } from "react";
import {
  getArtistTypes,
  createArtistType,
  updateArtistType,
  deleteArtistType,
} from "../../services/artistTypeService";

interface ArtistType {
  artistTypeId: number;
  artistTypeName: string;
}

const defaultForm: Partial<ArtistType> = { artistTypeId: 0, artistTypeName: "" };

const ArtistTypesPage: React.FC = () => {
  const [artistTypes, setArtistTypes] = useState<ArtistType[]>([]);
  const [form, setForm] = useState<Partial<ArtistType>>(defaultForm);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getArtistTypes();
      setArtistTypes(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e?.message || "Failed to load artist types");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.artistTypeName) return;
    setLoading(true);
    try {
      if (editingId) {
        await updateArtistType(editingId, form.artistTypeName);
      } else {
        await createArtistType(form.artistTypeName);
      }
      setForm(defaultForm);
      setEditingId(null);
      await load();
    } catch (e: any) {
      setError(e?.message || "Failed to save artist type");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (type: ArtistType) => {
    setForm(type);
    setEditingId(type.artistTypeId);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete this artist type?")) return;
    setLoading(true);
    try {
      await deleteArtistType(id);
      await load();
    } catch (e: any) {
      setError(e?.message || "Failed to delete artist type");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setForm(defaultForm);
    setEditingId(null);
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold mb-4 text-primary">Artist Types</h1>
      <form onSubmit={handleSubmit} className="mb-6 flex gap-2 items-end">
        <div>
          <label className="block text-sm text-secondary mb-1">Artist Type Name</label>
          <input
            name="artistTypeName"
            value={form.artistTypeName || ""}
            onChange={handleChange}
            className="border rounded px-3 py-2 text-sm w-64"
            required
          />
        </div>
        <button
          type="submit"
          className="bg-primary text-white rounded px-4 py-2 font-semibold"
          disabled={loading}
        >
          {editingId ? "Update" : "Add"}
        </button>
        {editingId && (
          <button type="button" onClick={handleCancel} className="ml-2 text-sm text-secondary underline">
            Cancel
          </button>
        )}
      </form>
      {error && <div className="text-red-600 mb-2">{error}</div>}
      <div className="bg-white shadow rounded p-4">
        <table className="min-w-full text-sm">
          <thead>
            <tr>
              <th className="py-2 text-left">ID</th>
              <th className="py-2 text-left">Name</th>
              <th className="py-2 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {artistTypes.map((type) => (
              <tr key={type.artistTypeId} className="border-t">
                <td className="py-2">{type.artistTypeId}</td>
                <td className="py-2">{type.artistTypeName}</td>
                <td className="py-2 text-center">
                  <button
                    onClick={() => handleEdit(type)}
                    className="text-primary hover:underline mr-2"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(type.artistTypeId)}
                    className="text-red-600 hover:underline"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {artistTypes.length === 0 && (
              <tr>
                <td colSpan={3} className="text-center py-4 text-secondary">No artist types found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ArtistTypesPage;
